<script setup>
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Editor from 'dashboard/components-next/Editor/Editor.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();
const store = useStore();

const formState = {
  labels: useMapGetter('labels/getLabels'),
};

onMounted(async () => {
  try {
    await store.dispatch('labels/get');
  } catch (error) {
    console.error('Failed to load labels:', error);
  }
});

const initialState = {
  handoffMessage: '',
  resolutionMessage: '',
  temperature: 1,
  autoResolveConversations: true,
  disabledLabels: [],
  disabledLabelType: 'conversation',
  enabledOnlyLabels: [],
  enabledOnlyLabelType: 'conversation',
};

const state = reactive({ ...initialState });

const validationRules = {
  handoffMessage: { minLength: minLength(1) },
  resolutionMessage: { minLength: minLength(1) },
};

const v$ = useVuelidate(validationRules, state);

const getErrorMessage = field => {
  return v$.value[field].$error ? v$.value[field].$errors[0].$message : '';
};

const formErrors = computed(() => ({
  handoffMessage: getErrorMessage('handoffMessage'),
  resolutionMessage: getErrorMessage('resolutionMessage'),
}));

const labelOptions = computed(() =>
  formState.labels.value?.map(label => ({
    value: label.title,
    label: label.title,
  })) || []
);

const labelTypeOptions = [
  { value: 'conversation', label: 'Conversation labels' },
  { value: 'contact', label: 'Contact labels' },
];

const updateStateFromAssistant = assistant => {
  const { config = {} } = assistant;
  state.handoffMessage = config.handoff_message;
  state.resolutionMessage = config.resolution_message;
  state.temperature = config.temperature || 1;
  // Handle both boolean and string values, default to true if not set
  const autoResolve = config.auto_resolve_conversations;
  state.autoResolveConversations =
    autoResolve === undefined || autoResolve === null
      ? true
      : autoResolve === 'false'
        ? false
        : Boolean(autoResolve);

  // Label filters
  state.disabledLabels = config.disabled_labels || [];
  state.disabledLabelType = config.disabled_label_type || 'conversation';
  state.enabledOnlyLabels = config.enabled_only_labels || [];
  state.enabledOnlyLabelType = config.enabled_only_label_type || 'conversation';
};

const handleSystemMessagesUpdate = async () => {
  const result = await Promise.all([
    v$.value.handoffMessage.$validate(),
    v$.value.resolutionMessage.$validate(),
  ]).then(results => results.every(Boolean));
  if (!result) return;

  const payload = {
    config: {
      ...props.assistant.config,
      handoff_message: state.handoffMessage,
      resolution_message: state.resolutionMessage,
      temperature: state.temperature || 1,
      auto_resolve_conversations: state.autoResolveConversations,
      disabled_labels: state.disabledLabels,
      disabled_label_type: state.disabledLabelType,
      enabled_only_labels: state.enabledOnlyLabels,
      enabled_only_label_type: state.enabledOnlyLabelType,
    },
  };

  emit('submit', payload);
};

watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) updateStateFromAssistant(newAssistant);
  },
  { immediate: true, deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-6">
    <Editor
      v-model="state.handoffMessage"
      :label="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')"
      :message="formErrors.handoffMessage"
      :message-type="formErrors.handoffMessage ? 'error' : 'info'"
    />

    <Editor
      v-model="state.resolutionMessage"
      :label="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.LABEL')"
      :placeholder="t('CAPTAIN.ASSISTANTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')"
      :message="formErrors.resolutionMessage"
      :message-type="formErrors.resolutionMessage ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.LABEL') }}
      </label>
      <div class="flex items-center gap-4">
        <input
          v-model="state.temperature"
          type="range"
          min="0"
          max="1"
          step="0.1"
          class="w-full"
        />
        <span class="text-sm text-n-slate-12">{{ state.temperature }}</span>
      </div>
      <p class="text-sm text-n-slate-11 italic">
        {{ t('CAPTAIN.ASSISTANTS.FORM.TEMPERATURE.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.LABEL') }}
          </label>
          <p class="text-sm text-n-slate-11 italic">
            {{ t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.DESCRIPTION') }}
          </p>
        </div>
        <Switch v-model="state.autoResolveConversations" />
      </div>
    </div>

    <!-- Label Filters Section -->
    <div class="flex flex-col gap-4 border-t border-n-slate-6 pt-6">
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          Label Filters
        </h3>
        <p class="text-sm text-n-slate-11 italic">
          Control which conversations the AI should respond to based on labels
        </p>
      </div>

      <!-- Don't respond to labels -->
      <div class="flex flex-col gap-3">
        <label class="text-sm font-medium text-n-slate-12">
          Don't respond to these labels
        </label>
        <p class="text-sm text-n-slate-11 italic mb-2">
          AI won't respond to conversations/contacts with these labels
        </p>

        <div class="flex flex-col gap-2">
          <label class="text-xs text-n-slate-11">
            Label type
          </label>
          <ComboBox
            v-model="state.disabledLabelType"
            :options="labelTypeOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-xs text-n-slate-11">
            Select labels
          </label>
          <TagMultiSelectComboBox
            v-model="state.disabledLabels"
            :options="labelOptions"
            label="Labels to exclude"
            placeholder="Select labels to exclude"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>

      <!-- Only respond to labels -->
      <div class="flex flex-col gap-3">
        <label class="text-sm font-medium text-n-slate-12">
          Only respond to these labels
        </label>
        <p class="text-sm text-n-slate-11 italic mb-2">
          AI will only respond to conversations/contacts with these labels (takes precedence over excluded labels)
        </p>

        <div class="flex flex-col gap-2">
          <label class="text-xs text-n-slate-11">
            Label type
          </label>
          <ComboBox
            v-model="state.enabledOnlyLabelType"
            :options="labelTypeOptions"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>

        <div class="flex flex-col gap-2">
          <label class="text-xs text-n-slate-11">
            Select labels
          </label>
          <TagMultiSelectComboBox
            v-model="state.enabledOnlyLabels"
            :options="labelOptions"
            label="Labels to include only"
            placeholder="Select labels to respond to only"
            class="[&>div>button]:bg-n-alpha-black2"
          />
        </div>
      </div>
    </div>

    <div>
      <Button
        :label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
        @click="handleSystemMessagesUpdate"
      />
    </div>
  </div>
</template>
